// Copyright 2025, University of Maryland, All Rights Reserved
import type { SectionFieldData } from './bill-analysis-parser'
import type { JSONSchema } from '@/api/types/schema'
import { parseAnalysisData } from './bill-analysis-parser'

/**
 * Render individual field values to HTML
 */
export function renderFieldValueToHTML(field: SectionFieldData): string {
  if (field.value === null || field.value === undefined) {
    return '<span class="text-muted-foreground">Not specified</span>'
  }

  switch (field.type) {
    case 'text':
      return `
        <div class="text-sm">
          ${field.title ? `<h4 class="font-semibold text-base mb-2 text-foreground">${field.title}:</h4>` : ''}
          <p class="text-muted-foreground leading-relaxed">${String(field.value)}</p>
        </div>
      `
    
    case 'integer':
      return `
        <div class="flex items-center gap-2">
          <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-semibold">${field.value}</span>
          ${field.title ? `<span class="text-sm font-medium text-muted-foreground">${field.title}</span>` : ''}
        </div>
      `
    
    case 'score': {
      console.log('Score field value:', field.value)
      
      // Handle both {score, score_type} and {type, score} formats
      let score, score_type
      
      if (field.value && typeof field.value === 'object') {
        if ('score' in field.value && 'score_type' in field.value) {
          score = field.value.score
          score_type = field.value.score_type
        } else if ('type' in field.value && 'score' in field.value) {
          score = field.value.score
          score_type = field.value.type
        } else {
          score = 'N/A'
          score_type = 'unknown'
        }
      } else {
        score = 'N/A'
        score_type = 'unknown'
      }
      
      const score_class = score_type === 'positive' ? 'bg-green-100 text-green-800' : 
                         score_type === 'negative' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
      return `
        <div class="flex items-center gap-3">
          <span class="${score_class} px-2 py-1 rounded text-sm font-semibold border">Score: ${score} (${score_type})</span>
          ${field.title ? `<span class="text-sm font-medium text-muted-foreground">${field.title}</span>` : ''}
        </div>
      `
    }
    
    case 'enum': {
      console.log('Enum field value:', field.value)
      
      // Handle legacy AIImpact objects with {type, score} structure
      let display_value
      if (field.value && typeof field.value === 'object' && 'type' in field.value && 'score' in field.value) {
        display_value = `${field.value.type} (${field.value.score})`
      } else {
        display_value = String(field.value)
      }
      
      const enum_class = display_value.toLowerCase().includes('positive') ? 'bg-green-100 text-green-800' :
                        display_value.toLowerCase().includes('negative') ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
      return `
        <div class="flex items-center gap-2">
          <span class="${enum_class} px-2 py-1 rounded text-sm">${display_value}</span>
          ${field.title ? `<span class="text-sm font-medium text-muted-foreground">${field.title}</span>` : ''}
        </div>
      `
    }
    
    case 'text_list': {
      const listItems = Array.isArray(field.value) ? field.value : [field.value]
      return `
        <div class="text-sm">
          ${field.title ? `<h4 class="font-semibold text-base mb-3 text-foreground">${field.title}:</h4>` : ''}
          <ul class="list-disc list-inside space-y-2 text-muted-foreground">
            ${listItems.map((item: any) => `<li>${String(item)}</li>`).join('')}
          </ul>
        </div>
      `
    }
    
    case 'object_list': {
      const objectItems = Array.isArray(field.value) ? field.value : [field.value]
      
      if (Array.isArray(field.value) && field.value.length === 0) {
        return `
          <div class="text-sm">
            ${field.title ? `<h4 class="font-semibold text-base mb-3 text-foreground">${field.title}:</h4>` : ''}
            <div class="text-muted-foreground italic text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
              No items found
            </div>
          </div>
        `
      }
      
      return `
        <div class="text-sm">
          ${field.title ? `<h4 class="font-semibold text-base mb-3 text-foreground">${field.title}:</h4>` : ''}
          <div class="space-y-3">
            ${objectItems.map((obj: any, index: number) => `
              <div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
                ${objectItems.length > 1 ? `<div class="text-xs text-muted-foreground mb-2 font-medium">Item ${index + 1}</div>` : ''}
                <div class="space-y-2">
                  ${typeof obj === 'object' && obj !== null ?
                    Object.entries(obj).map(([key, value]) => {
                      const property_title = field.object_properties?.[key]?.title || key.replace(/_/g, ' ')
                      const property_type = field.object_properties?.[key]?.type
                      
                      // Handle different value types properly
                      let display_value
                      if (typeof value === 'object' && value !== null && 'score' in value && 'score_type' in value) {
                        const score_class = value.score_type === 'positive' ? 'bg-green-100 text-green-800' : 
                                           value.score_type === 'negative' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        display_value = `<span class="${score_class} px-2 py-1 rounded text-xs font-semibold">${value.score} (${value.score_type})</span>`
                      } else if (property_type === 'integer' && typeof value === 'number') {
                        display_value = `<span class="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">${value}</span>`
                      } else if (Array.isArray(value)) {
                        display_value = `<ul class="list-disc list-inside ml-3">${value.map(item => `<li class="text-sm">${String(item)}</li>`).join('')}</ul>`
                      } else {
                        display_value = `<span class="text-muted-foreground">${String(value)}</span>`
                      }
                      
                      return `
                        <div class="text-sm">
                          <span class="font-medium text-foreground">${property_title}:</span>
                          <span class="ml-2">${display_value}</span>
                        </div>
                      `
                    }).join('') :
                    `<span class="text-sm text-muted-foreground">${String(obj)}</span>`
                  }
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `
    }
    
    default:
      return `<span class="text-sm text-muted-foreground">${String(field.value)}</span>`
  }
}

/**
 * HTML renderer for bill sections data using the same logic as BillSections.tsx
 */
export function renderBillDataToHTML(report_data: any, schema?: JSONSchema): string {
  if (!report_data || typeof report_data !== 'object') {
    return '<div class="text-center py-8 text-muted-foreground"><p>No report data available.</p></div>'
  }

  // Use the shared parseAnalysisData function
  const sections = parseAnalysisData(report_data, schema)

  if (sections.length === 0) {
    return '<div class="text-center py-8 text-muted-foreground"><p>No sections found in the report data.</p></div>'
  }

  return `
    <div class="grid gap-6 pb-6">
      ${sections.map(section => `
        <div class="shadow-sm">
          ${section.title ? `
            <div class="pb-3 p-4">
              <h3 class="font-semibold text-lg">${section.title}</h3>
            </div>
          ` : ''}
          <div class="space-y-4 p-4 pt-0">
            ${section.fields.map((field, index) => `
              <div>
                ${renderFieldValueToHTML(field)}
                ${index < section.fields.length - 1 ? '<hr class="mt-3 border-gray-200">' : ''}
              </div>
            `).join('')}
          </div>
        </div>
      `).join('')}
    </div>
  `
}